import { createStore } from "vuex";
import proctoring from "./useProctoringStore";
import examEligibility from "./examEligibilityStore";
import axios from "axios";
import createPersistedState from "vuex-persistedstate";
import SecureLS from "secure-ls";
import { resetThemeColor } from "@/utils/themeColor";
import { resolveActiveRole, deriveDashboardAccess } from "@/utils/roleAccess";

const ls = new SecureLS({ isCompression: false });

function decodeTokenExp(token) {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join("")
  );
  const decoded = JSON.parse(jsonPayload);

  return new Date(decoded.exp * 1000);
}

export default createStore({
  modules: {
    proctoring,
    examEligibility,
  },

  state: {
    access: null,
    refresh: null,
    user: null,
    roles: [],
    dashboardAccess: [],
    activeRole: null,
    isAuthenticated: false,
    accessToken: null,
    refreshToken: null,
    tokenExp: null,
    refreshTokenExp: null,
    josh_id: null,
    loginError: null,
    is_face: false,
    is_nri: false,
    isSsoLogin: false,
    themeColors: {},
    sessionExpired: false,
  },

  getters: {
    isAuthenticated: (state) => state.isAuthenticated,
    user: (state) => state.user,
    access: (state) => state.access,
    refresh: (state) => state.refresh,
    roles: (state) => state.roles,
    dashboardAccess: (state) => state.dashboardAccess,
    activeRole: (state) => state.activeRole,
    themeColors: (state) => state.themeColors,
    getAccessToken: (state) => state.accessToken,
    getRefreshToken: (state) => state.refreshToken,
    getTokenExp: (state) => state.tokenExp,
    getRefreshTokenExp: (state) => state.refreshTokenExp,
    josh_id: (state) => state.josh_id,
    login_error: (state) => state.loginError,
    is_face: (state) => state.is_face,
    is_nri: (state) => state.is_nri,
    isSsoLogin: (state) => state.isSsoLogin,
    sessionExpired: (state) => state.sessionExpired,
  },

  mutations: {
    setLoggedInInfo(state, payload) {
      const userData = payload.data;

      state.isAuthenticated = true;

      state.access = userData.access;
      state.refresh = userData.refresh;

      state.user = {
        id: userData.user_id,
        student_profile_id: userData.student_profile_id,
        exam_guidelines: userData.exam_guidelines,
        full_name: userData.full_name,
        email: userData.email,
        phone: userData.phone,
        face_reference: userData.user_image,
      };

      state.roles = userData.roles || [];
      state.dashboardAccess = deriveDashboardAccess(userData);
      state.activeRole = resolveActiveRole(
        state.dashboardAccess,
        state.activeRole
      );
      state.is_face = userData.is_face || false;
      state.is_nri = userData.is_nri || false;
    },

    setActiveRole(state, role) {
      if (state.dashboardAccess.includes(role)) {
        state.activeRole = role;
      }
    },

    SET_EXAM_GUIDELINES_ACKNOWLEDGED(state) {
      if (state.user) {
        state.user.exam_guidelines = true;
      }
    },

    setLogout(state) {
      state.access = null;
      state.refresh = null;
      state.user = null;
      state.roles = [];
      state.dashboardAccess = [];
      state.activeRole = null;
      state.isAuthenticated = false;
      state.accessToken = null;
      state.refreshToken = null;
      state.tokenExp = null;
      state.refreshTokenExp = null;
      state.josh_id = null;
      state.loginError = null;
      state.is_face = false;
      state.is_nri = false;
      state.isSsoLogin = false;
      resetThemeColor();

      localStorage.removeItem("exam_id");
      localStorage.removeItem("exam_name");
      localStorage.removeItem("room_id");
      localStorage.removeItem("room_name");
      localStorage.removeItem("exam_attempt_id");
    },
    setAccessToken(state, token) {
      state.accessToken = token;
    },
    setRefreshToken(state, token) {
      state.refreshToken = token;
    },
    setExpDate(state, date) {
      state.tokenExp = date;
    },
    setRefreshTokenExp(state, date) {
      state.refreshTokenExp = date;
    },
    setJoshInfo(state, data) {
      state.josh_id = data;
    },
    setLoginError(state, data) {
      state.loginError = data;
    },
    setIsFace(state, isFace) {
      state.is_face = isFace;
    },
    setIsNri(state, isNri) {
      state.is_nri = isNri;
    },
    updateAccessToken(state, token) {
      state.access = token;
      state.accessToken = token;
    },
    setUserFaceReference(state, faceReference) {
      if (state.user) {
        state.user.face_reference = faceReference;
      }
    },
    setSsoLogin(state, value) {
      state.isSsoLogin = value;
    },
    setThemeColor(state, { role, color }) {
      state.themeColors = { ...state.themeColors, [role]: color };
    },
    setSessionExpired(state, value) {
      state.sessionExpired = value;
    },
  },

  actions: {
    async loginWithPassword({ commit, dispatch }, data) {
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ROOT}/auth/login/`,
          data
        );

        commit("setLoggedInInfo", response.data);
        commit("setAccessToken", response.data.data.access);
        commit("setRefreshToken", response.data.data.refresh);
        commit("setSsoLogin", false);
        if (response.data.data.josh_id != null) {
          commit("setJoshInfo", response.data.data.josh_id);
        }

        dispatch("saveToken");

        return response.data;
      } catch (error) {
        const message =
          error.response?.data?.message ||
          error.response?.data?.error?.detail ||
          "Login failed";

        commit("setLoginError", message);

        return {
          status: error.response?.status,
          message,
        };
      }
    },
    async sendStudentOtp(_, { email }) {
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ROOT}/auth/login/send-otp/`,
          { email }
        );
        return response.data;
      } catch (error) {
        console.error("Send OTP Error:", error);
        return error.response?.data ?? null;
      }
    },

    async loginWithOtp({ commit, dispatch }, { email, otp }) {
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ROOT}/auth/login/verify-otp/`,
          { email, otp }
        );
        commit("setLoggedInInfo", response.data);
        commit("setAccessToken", response.data.data.access);
        commit("setRefreshToken", response.data.data.refresh);
        commit("setSsoLogin", false);
        if (response.data.data.josh_id != null) {
          commit("setJoshInfo", response.data.data.josh_id);
        }
        dispatch("saveToken");
        return response.data;
      } catch (error) {
        console.error("OTP Login Error:", error);
        return error.response?.data ?? null;
      }
    },

    async loginWithSsoToken({ commit, dispatch }, token) {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_ROOT}/auth/me/`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        commit("setLoggedInInfo", response.data);
        commit("setAccessToken", response.data.data.access);
        commit("setRefreshToken", response.data.data.refresh);
        commit("setSsoLogin", true);
        if (response.data.data.josh_id != null) {
          commit("setJoshInfo", response.data.data.josh_id);
        }

        dispatch("saveToken");

        return response.data;
      } catch (error) {
        const message =
          error.response?.data?.message ||
          error.response?.data?.error?.detail ||
          error.response?.data?.detail ||
          "SSO login failed";

        commit("setLoginError", message);

        return {
          status: error.response?.status,
          message,
        };
      }
    },
    logout({ commit }, { expired = false } = {}) {
      commit("setLogout");
      commit("setExpDate", null);
      commit("setSessionExpired", expired);
    },
    saveToken({ state, commit }) {
      const token = state.accessToken;
      if (token) {
        try {
          commit("setExpDate", decodeTokenExp(token));
        } catch (err) {
          console.error("Failed to decode access token JWT:", err);
        }
      }

      const refreshToken = state.refreshToken;
      if (refreshToken) {
        try {
          commit("setRefreshTokenExp", decodeTokenExp(refreshToken));
        } catch (err) {
          console.error("Failed to decode refresh token JWT:", err);
        }
      }
    },
    async refreshAccessToken({ state, commit, dispatch }) {
      const refresh = state.refresh || state.refreshToken;
      if (!refresh) return null;

      // Always ask the server — it's the source of truth on whether the
      // refresh token is still valid, not our locally decoded JWT exp
      // (client clock skew / stale persisted state shouldn't end a session
      // the server would otherwise still honor).
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ROOT}/auth/refresh/`,
          { refresh }
        );

        if (response.data && response.data.access) {
          commit("updateAccessToken", response.data.access);
          dispatch("saveToken");
          return response.data.access;
        }

        dispatch("logout", { expired: true });
        return null;
      } catch (error) {
        console.error("Token refresh failed:", error);
        dispatch("logout", { expired: true });
        return null;
      }
    },
  },

  plugins: [
    createPersistedState({
      key: "exam-app",
      storage: {
        getItem: (key) => {
          try {
            return ls.get(key);
          } catch (error) {
            console.warn(
              "Corrupted persisted state, resetting to defaults",
              error
            );
            try {
              ls.remove(key);
            } catch (_removeError) {
              // ignore — falling through to a fresh, unauthenticated state either way
            }
            return null;
          }
        },
        setItem: (key, value) => ls.set(key, value),
        removeItem: (key) => ls.remove(key),
      },
      // Module mutations (eligibility polling, proctoring) never touch the
      // persisted paths, so skip re-encrypting and rewriting storage for them.
      filter: (mutation) => !mutation.type.includes("/"),
      paths: [
        "access",
        "refresh",
        "user",
        "roles",
        "dashboardAccess",
        "activeRole",
        "isAuthenticated",
        "accessToken",
        "refreshToken",
        "tokenExp",
        "refreshTokenExp",
        "is_face",
        "is_nri",
        "isSsoLogin",
        "themeColors",
      ],
    }),
  ],
});
